/* eslint-disable @next/next/no-img-element */
import {notification} from 'antd';
import useCommon from '@utils/hooks/common';
import {useRouter} from 'next/router';
import authApi from '@utils/api/auth.api';
import styles from './styles';
import {useAccount, useSignMessage} from 'wagmi';
import {disconnect} from '@wagmi/core';
import WalletConnectConfig from '@configs/WalletConnectConfig';
import {useEffect} from 'react';
import PopupContainer from '../PopupContainer';
import {clearLocalStorageExceptDontShowDisclaimerAgain} from '@utils/index';

const buttonContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '16px',
  gap: '20px', // Add gap between buttons
};

const commonButtonStyle = {
  width: '120px',
  height: '48px',
  borderRadius: '24px', // For round shape
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontSize: '16px', // Ensure a readable font size
  fontWeight: '600', // Increase font weight
};

const cancelButtonStyle = {
  ...commonButtonStyle,
  backgroundColor: 'transparent',
  color: 'white',
  border: '2px solid rgba(255, 255, 255, 0.6)', // Increase border size
};

const signButtonStyle = signing => ({
  ...commonButtonStyle,
  backgroundColor: signing ? 'rgba(74, 103, 248, 0.6)' : '#4a67f8',
  color: 'white',
  border: '2px solid transparent', // Ensure no border by default, but you can customize if needed
  cursor: signing ? 'not-allowed' : 'pointer',
});

const ButtonGroup = ({isSignButtonHidden, cancelSignMessage, requestSignMessage}) => {
  const {t} = useCommon({
    withTranslation: ['auth'],
  });

  return (
    <div style={buttonContainerStyle}>
      <button type="button" style={cancelButtonStyle} onClick={cancelSignMessage}>
        <span>{t('cancel')}</span>
      </button>

      {!isSignButtonHidden && (
        <button
          type="button"
          style={signButtonStyle(isSignButtonHidden)}
          onClick={() => {
            console.log(' ** WalletConnectSignMessageModalSheet - onClick');
            requestSignMessage();
          }}
          disabled={isSignButtonHidden}
        >
          <span>{t('sign')}</span>
        </button>
      )}
    </div>
  );
};

interface CustomModalSheetProps {
  isModalSheetOpen: boolean;
  onModalSheetClose: () => void;
}

const WalletConnectSignMessageModalSheet = (props: CustomModalSheetProps) => {
  const {classes, t, system} = useCommon({
    withTranslation: ['auth'],
    withStyles: styles,
    observers: ['system'],
  });
  const signMessageHook = useSignMessage();

  const {isModalSheetOpen, onModalSheetClose} = props;

  const router = useRouter();
  const {isConnected, address} = useAccount();
  // * below disconnect can cause router.push issue
  // const {disconnect} = useDisconnect();

  const cancelSignMessage = async () => {
    await disconnect(WalletConnectConfig);
    clearLocalStorageExceptDontShowDisclaimerAgain();
    system?.dismissModalSheet();
    // router.push('/home');
    router.push(router.pathname);
  };

  const loginByWallet = async ({signature, message}) => {
    console.log(' ^^ loginByWallet');
    try {
      // const simulateApiResponse = () =>
      //   new Promise(
      //     resolve =>
      //       setTimeout(() => {
      //         resolve({
      //           success: true,
      //           data: {
      //             playerToken: '',
      //             gatewayToken: 'exampleGatewayToken',
      //           },
      //         });
      //       }, 2000)
      //   );
      // const response = await simulateApiResponse();
      const response = await authApi.loginWithWalletConnect({signature, message});
      console.log(' loginByWallet - loginWithWalletConnect', response);

      if (response.success) {
        const {playerToken} = response.data;

        if (!playerToken) {
          notification.error({
            message: t('error_messages.missing_player_token'),
            description: t('error_messages.please_try_again'),
          });
          cancelSignMessage();
          return;
        }

        if (router.pathname === '/fortune-wheel') {
          router.push({
            pathname: '/login-with-token',
            query: {
              playerToken: response.data.playerToken,
              isReload: false,
              isLuckyDraw: true,
              ...router.query,
            },
          });
        } else {
          router.push({
            pathname: '/login-with-token',
            query: {
              playerToken: response.data.playerToken,
              isReload: false,
              ...router.query,
            },
          });
        }
      }
    } catch (error) {
      cancelSignMessage();
      notification.error({
        message: error?.response?.data?.error?.message || t('error_messages.error_occurred'),
      });
    } finally {
      system?.dismissModalSheet();
    }
  };

  const requestSignMessage = async () => {
    console.log('## requestSignMessage');

    try {
      if (!isConnected) {
        console.log('## Not connected');
        // await disconnect(WalletConnectConfig);
        clearLocalStorageExceptDontShowDisclaimerAgain();
        system?.dismissModalSheet();
        // router.push('/home');
        router.push(router.pathname);
        return;
      }
      // Get the nonce
      const res = await authApi.getWalletConnectNonce();
      if (!res.success) {
        return '';
      }

      console.log('## getNonce - res', res, 'address: ', address);
      const {nonce} = res.data;
      const walletAddress = address;
      // Create the message to be signed
      const message = `Nonce: ${nonce}\nAddress: ${walletAddress}\nMessage: Please sign this message for verification.`;
      const requestSignMessage = {
        account: address,
        message,
      };
      // request wallet sign message
      // const signature = await signMessage(WalletConnectConfig, requestSignMessage);
      const signature = await signMessageHook.signMessageAsync(requestSignMessage);
      console.log(' ## signMessage', signature);

      if (signature) {
        notification.success({
          message: t('sign_message_success'),
          // description: 'Redirecting, thank your for your patience',
        });
        // system?.dismissModalSheet();
      }

      await loginByWallet({signature, message});
    } catch (err) {
      console.error('Error in requestSignMessage:', err);
    }
  };

  useEffect(() => {
    if (isModalSheetOpen) {
      requestSignMessage();
    }
  }, [isModalSheetOpen]);

  return (
    <PopupContainer
      visible={isModalSheetOpen}
      onCancel={() => {}}
      mobileModalProps={{
        disableScrollLocking: true,
        disableDrag: true,
        sheetDetent: 'content-height',
      }}
    >
      <div
        style={{
          padding: '16px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <img src="/images-mobile/LoginSelectButton/wallet-connect.svg" width={96} height={96} />
        <div className={classes.title}>{t('sign_request_in_wallet')}</div>
        <div className={classes.content}>{t('wallet_connect_sign_this_message')}</div>
        <ButtonGroup
          isSignButtonHidden={
            signMessageHook?.status === 'pending' || signMessageHook?.status === 'success'
          }
          cancelSignMessage={cancelSignMessage}
          requestSignMessage={requestSignMessage}
        />
      </div>
    </PopupContainer>
  );
};

export default WalletConnectSignMessageModalSheet;
